/**
 * Placement results: the latest result per language, cached on the device (keyed by user)
 * and saved to `placement_results`. A result that could not be saved (offline, server
 * not ready) stays marked `pending` and is retried on the next refresh.
 */
import { useEffect, useState } from 'react';
import type { PlacementBank, PlacementResult } from '../core/placement.ts';
import type { Json } from './database.types.ts';
import { supabase } from './supabase.ts';
import { readJson, writeJson } from './storage.ts';

/** Languages that have a placement item bank. */
export const PLACEMENT_LANGS: readonly string[] = ['zh', 'en'];

export interface PlacementScore {
  version: number;
  level: number;
  mean: number;
  sd: number;
  skills: PlacementResult['skills'];
  answered: number;
  correct: number;
  /** Recommended first lesson; lessons before it in the course are open. */
  startLessonId: string | null;
  bookId: string | null;
  note: string | null;
  /** Optional AI comment on the open answer. */
  aiComment?: string | null;
}

export interface PlacementRecord {
  lang: string;
  level_label: string;
  score: PlacementScore;
  taken_at: string;
  pending?: boolean;
}

export type PlacementMap = Record<string, PlacementRecord>;

const cacheKey = (userId: string) => `soile.placement.v1.${userId}`;
/** Languages whose test offer the learner dismissed on this device. */
const dismissedKey = (userId: string) => `soile.placement.dismissed.v1.${userId}`;

let cache: { userId: string; map: PlacementMap } | null = null;
/** Users whose results were already refreshed in this page session (one round trip). */
const refreshed = new Set<string>();
const listeners = new Set<(map: PlacementMap) => void>();

export function cachedPlacements(userId: string): PlacementMap {
  if (cache?.userId !== userId) cache = { userId, map: readJson<PlacementMap>(cacheKey(userId)) ?? {} };
  return cache.map;
}

function store(userId: string, map: PlacementMap): void {
  cache = { userId, map };
  writeJson(cacheKey(userId), map);
  listeners.forEach((l) => l(map));
}

async function insert(record: PlacementRecord): Promise<boolean> {
  const { error } = await supabase.from('placement_results').insert({
    lang: record.lang,
    level_label: record.level_label,
    score: record.score as unknown as Json,
    taken_at: record.taken_at,
  });
  return !error;
}

/** Saves a finished test: on the device at once, on the server when possible. */
export async function savePlacement(userId: string, record: PlacementRecord): Promise<void> {
  store(userId, { ...cachedPlacements(userId), [record.lang]: { ...record, pending: true } });
  if (await insert(record).catch(() => false)) {
    store(userId, { ...cachedPlacements(userId), [record.lang]: { ...record, pending: false } });
  }
}

/** Sends pending results, then takes the server's latest per language (newer local wins). */
export async function refreshPlacements(userId: string): Promise<void> {
  const local = cachedPlacements(userId);
  for (const record of Object.values(local)) {
    if (record.pending && (await insert(record).catch(() => false))) {
      store(userId, { ...cachedPlacements(userId), [record.lang]: { ...record, pending: false } });
    }
  }
  const { data, error } = await supabase
    .from('placement_results')
    .select('lang, level_label, score, taken_at')
    .order('taken_at', { ascending: false })
    .limit(20);
  if (error || !data) return;
  const merged: PlacementMap = { ...cachedPlacements(userId) };
  for (const row of data) {
    const known = merged[row.lang];
    if (known && known.taken_at >= row.taken_at) continue;
    if (typeof row.score !== 'object' || row.score === null || Array.isArray(row.score)) continue;
    merged[row.lang] = { lang: row.lang, level_label: row.level_label, score: row.score as unknown as PlacementScore, taken_at: row.taken_at };
  }
  store(userId, merged);
}

export function usePlacements(userId: string): PlacementMap {
  const [map, setMap] = useState<PlacementMap>(() => cachedPlacements(userId));
  useEffect(() => {
    setMap(cachedPlacements(userId));
    const listener = (next: PlacementMap) => setMap(next);
    listeners.add(listener);
    if (!refreshed.has(userId)) {
      refreshed.add(userId);
      void refreshPlacements(userId).catch(() => undefined);
    }
    return () => {
      listeners.delete(listener);
    };
  }, [userId]);
  return map;
}

export function placementDismissed(userId: string, lang: string): boolean {
  return (readJson<string[]>(dismissedKey(userId)) ?? []).includes(lang);
}

export function dismissPlacement(userId: string, lang: string): void {
  const list = readJson<string[]>(dismissedKey(userId)) ?? [];
  if (!list.includes(lang)) writeJson(dismissedKey(userId), [...list, lang]);
}

/** Item banks are loaded on demand: they are only needed while taking the test. */
export async function loadPlacementBank(lang: string): Promise<PlacementBank | null> {
  switch (lang) {
    case 'zh':
      return (await import('../content/placement/zh.json')).default as unknown as PlacementBank;
    case 'en':
      return (await import('../content/placement/en.json')).default as unknown as PlacementBank;
    default:
      return null;
  }
}
